import React, { FunctionComponent, useReducer, useEffect, useCallback, useRef } from 'react';

import { IReducerAction } from '@dw/models/shared';
import { IUser, USER_TYPE } from '@dw/models/user';
import { useInitializeAuthMutation } from '@dw/hooks/api/AuthApi';
import { useSession } from '@dw/hooks';
import { useGetSelf } from '@dw/hooks/api/UserApi';
import { IDanceSchool } from '@dw/models/danceSchool';
import { useGetDanceSchool } from '@dw/hooks/api/DanceSchoolApi';
import { useQueryClient } from '@tanstack/react-query';
import { IDanceSchoolUser, USER_ROLE } from '@dw/models/danceSchoolUser';
import { IInitializeAuthResponse } from '@dw/models/auth';

type TAuthContextActions = 'SET_USER' | 'SET_DANCE_SCHOOL' | 'SET_DANCE_SCHOOL_USER' | 'SET_INITIALIZED' | 'SET_IS_AUTHENTICATING';

interface IAuthContext {
  isAuthenticated: boolean;
  isInitialized: boolean;
  isAuthenticating: boolean;
  user?: IUser;
  danceSchool?: IDanceSchool;
  danceSchoolUser?: IDanceSchoolUser;

  setAuthDetails: (authDetails: IInitializeAuthResponse) => void;
  userHasRole: (...roles: USER_ROLE[]) => boolean;
  userHasExactRole: (...roles: USER_ROLE[]) => boolean;
}
const initialAuthState: IAuthContext = {
  isAuthenticated: false,
  isInitialized: false,
  isAuthenticating: false, 
  setAuthDetails: () => {},
  userHasRole: () => false,
  userHasExactRole: () => false
};
export const AuthContext = React.createContext(initialAuthState);

/**
 * The bootstrap snapshot is the last successful `/v1/auth/initialize` response. Hydrating from it
 * lets the dashboard paint on a cold load; the background initialize below reconciles it a moment
 * later, so a stale snapshot is corrected rather than trusted.
 */
const createInitialState = (bootstrapSnapshot: IInitializeAuthResponse | null): IAuthContext => {
  if (!bootstrapSnapshot) {
    return { ...initialAuthState };
  }

  return {
    ...initialAuthState,
    isAuthenticated: true,
    isInitialized: true,
    user: bootstrapSnapshot.user,
    danceSchool: bootstrapSnapshot.activeDanceSchoolUser?.danceSchool,
    danceSchoolUser: bootstrapSnapshot.activeDanceSchoolUser
  };
};

const AuthContextReducer = (
  state: IAuthContext,
  action: IReducerAction<TAuthContextActions>
): IAuthContext => {
  switch (action.type) {
    case 'SET_USER':
      return { ...state, user: action.payload, isAuthenticated: !!action.payload };
    case 'SET_DANCE_SCHOOL':
      return { ...state, danceSchool: action.payload };
    case 'SET_DANCE_SCHOOL_USER':
      return { ...state, danceSchoolUser: action.payload };
    case 'SET_INITIALIZED':
      return { ...state, isInitialized: action.payload };
    case 'SET_IS_AUTHENTICATING':
      return { ...state, isAuthenticating: action.payload };
    default:
      return state;
  }
};

interface IAuthContextProviderProps {
  children: React.ReactNode;
}
export const AuthContextProvider: FunctionComponent<
  IAuthContextProviderProps
> = (props: IAuthContextProviderProps) => {
  
  const queryClient = useQueryClient();

  const {
    isAuthorized: isSessionAuthorized,
    isInitialized: isSessionInitialized,
    authorizedUser,
    bootstrapSnapshot,
    persistBootstrapSnapshot
  } = useSession();

  const [state, dispatch] = useReducer(AuthContextReducer, bootstrapSnapshot, createInitialState);

  const hydratedFromSnapshot = useRef(!!bootstrapSnapshot);

  const {
    mutate: initializePortalAuth,
    isPending: initializePortalAuthLoading,
    isSuccess: initializePortalAuthSuccess
  } = useInitializeAuthMutation({
    onSuccess: (response) => {
      persistBootstrapSnapshot(response);
      dispatch({ type: 'SET_USER', payload: response.user });
      dispatch({ type: 'SET_DANCE_SCHOOL', payload: response.activeDanceSchoolUser?.danceSchool });
      dispatch({ type: 'SET_DANCE_SCHOOL_USER', payload: response.activeDanceSchoolUser });
      dispatch({ type: 'SET_INITIALIZED', payload: true });
    },
    onError: () => {
      // State hydrated from the snapshot survives a failed reconcile — only a failed token refresh
      // (handled in the session provider) ends the session, so a blip must not eject the user.
      if (!hydratedFromSnapshot.current) {
        dispatch({ type: 'SET_USER', payload: null });
        dispatch({ type: 'SET_DANCE_SCHOOL', payload: null });
        dispatch({ type: 'SET_DANCE_SCHOOL_USER', payload: null });
      }
      dispatch({ type: 'SET_INITIALIZED', payload: true });
    }
  });

  const { data: getSelfResult } = useGetSelf({
    enabled: isSessionAuthorized && initializePortalAuthSuccess,
    autoShowErrorMessages: false,
    autoLogOutOnAuthError: !!state.user
  });

  const { data: getDanceSchoolResult } = useGetDanceSchool(state.danceSchool?.id ?? '', {
    enabled: isSessionAuthorized && initializePortalAuthSuccess,
    autoShowErrorMessages: false,
    autoLogOutOnAuthError: !!state.user
  });

  const userHasRole = useCallback((...roles: USER_ROLE[]) => {
    return roles.some(x => x === state.danceSchoolUser?.role);
  }, [state.danceSchoolUser]);

  const userHasExactRole = useCallback((...roles: USER_ROLE[]) => {
    if (!state.danceSchoolUser?.role) {
      return false;
    }

    return roles.includes(state.danceSchoolUser.role);
  }, [state.danceSchoolUser]);

  const setAuthDetails = useCallback((authDetails: IInitializeAuthResponse) => {
    if (authDetails.user) {
      dispatch({
        type: 'SET_USER',
        payload: authDetails.user
      });
    }

    if (authDetails.activeDanceSchoolUser) {
      dispatch({
        type: 'SET_DANCE_SCHOOL',
        payload: authDetails.activeDanceSchoolUser.danceSchool
      });
      dispatch({
        type: 'SET_DANCE_SCHOOL_USER',
        payload: authDetails.activeDanceSchoolUser
      });
    }
    // Switching schools produces a full initialize response — keep the snapshot in step so the next
    // cold load paints the school the user actually ended up in.
    persistBootstrapSnapshot(authDetails);
    queryClient.clear();
  }, [dispatch, queryClient, persistBootstrapSnapshot]);

  // Runs once per session identity: the session provider hands out the same `authorizedUser` object
  // across token rotations, so a refresh does not re-trigger this.
  //
  // Deliberately NOT guarded by a "already initialized for this id" ref. React's StrictMode
  // double-invokes this effect in development and tears the first mutation observer down with it, so
  // its `onSuccess` never lands — guarding the second call leaves the app running on the snapshot
  // with no reconcile, and with `useGetSelf`/`useGetDanceSchool` never enabled. The duplicate call is
  // development-only; a production build invokes this exactly once (verified against the network log).
  useEffect(() => {
    if (authorizedUser) {
      // Names come from the onboarding form via `PUT /v1/users/self`; the API back-fills and ignores
      // blanks here, exactly as it did for a social login with no display name.
      initializePortalAuth({
        firstName: '',
        lastName: '',
        userType: USER_TYPE.PORTAL
      });
    } else {
      dispatch({
        type: 'SET_USER',
        payload: null,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizedUser]);

  useEffect(() => {
    if (isSessionInitialized && !isSessionAuthorized) {
      dispatch({
        type: 'SET_INITIALIZED',
        payload: true
      });
    }
  }, [isSessionInitialized, isSessionAuthorized]);

  useEffect(() => {
    dispatch({
      type: 'SET_IS_AUTHENTICATING',
      payload: initializePortalAuthLoading
    });
  }, [initializePortalAuthLoading]);

  useEffect(() => {
    if (getSelfResult) {
      dispatch({
        type: 'SET_USER',
        payload: getSelfResult
      });

      if (window.newrelic) {
        window.newrelic?.setUserId(getSelfResult.id);
      }
    }
  }, [getSelfResult]);

  useEffect(() => {
    if (getDanceSchoolResult) {
      dispatch({
        type: 'SET_DANCE_SCHOOL',
        payload: getDanceSchoolResult
      });

      if (window.newrelic) {
        window.newrelic?.setCustomAttribute('danceSchoolId', getDanceSchoolResult.id);
      }
    }
  }, [getDanceSchoolResult]);

  return (
    <AuthContext.Provider value={{...state, setAuthDetails, userHasRole, userHasExactRole}}>{props.children}</AuthContext.Provider>
  );
};
